import React, { useState, useEffect } from 'react';
import { Download, Printer, Search, FileText, TrendingUp, TrendingDown } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const fmt = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;

const GroupProfitLoss = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const [trend, setTrend] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => { fetchColleges(); fetchReport(); }, []);
  useEffect(() => { fetchTrend(); }, [selectedCollege]);

  const fetchTrend = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/profit-loss-trend?months=12`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setTrend(res.data.trend || []);
    } catch (e) { console.error(e); }
  };

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/profit-loss?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.colleges?.length) return toast.error('No data to export');
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data.colleges.map(c => ({
      'College': c.college_name, 'Total Income': c.total_income, 'Total Expense': c.total_expense, 'Net Profit/Loss': c.net_profit
    })).concat([{ 'College': 'GROUP TOTAL', 'Total Income': data.totals?.total_income, 'Total Expense': data.totals?.total_expense, 'Net Profit/Loss': data.totals?.net_profit }]));
    XLSX.utils.book_append_sheet(wb, ws, 'Profit & Loss');
    const catRows = [
      ...Object.entries(data.income_by_category || {}).map(([cat, amt]) => ({ 'Type': 'Income', 'Category': cat, 'Amount': amt })),
      ...Object.entries(data.expense_by_category || {}).map(([cat, amt]) => ({ 'Type': 'Expense', 'Category': cat, 'Amount': amt }))
    ];
    if (catRows.length) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(catRows), 'Categories');
    XLSX.writeFile(wb, `Group_Profit_Loss_${fromDate}_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.colleges?.length) return toast.error('No data to export');
    const rows = data.colleges.map(c => [c.college_name, fmt(c.total_income), fmt(c.total_expense), fmt(c.net_profit)]);
    rows.push(['GROUP TOTAL', fmt(data.totals?.total_income), fmt(data.totals?.total_expense), fmt(data.totals?.net_profit)]);
    exportToPDF('Group Profit & Loss Statement',
      ['College', 'Total Income', 'Total Expense', 'Net Profit/Loss'],
      rows,
      `Group_Profit_Loss_${fromDate}_${toDate}`,
      `Period: ${fromDate} to ${toDate} | Net: ${fmt(data.totals?.net_profit)}`
    );
  };

  const netPositive = (data?.totals?.net_profit || 0) >= 0;

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="group-profit-loss">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Profit &amp; Loss</h1>
          <p className="text-sm text-gray-500">Consolidated profit &amp; loss across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="group-pl-excel-btn" onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button data-testid="group-pl-pdf-btn" onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">From</label><input data-testid="group-pl-from-date" type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">To</label><input data-testid="group-pl-to-date" type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select data-testid="group-pl-college-filter" value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button data-testid="group-pl-search-btn" onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-green-500">
              <p className="text-xs text-gray-500">Total Income (Group)</p>
              <p data-testid="group-pl-total-income" className="text-2xl md:text-3xl font-bold text-green-600">{fmt(data.totals?.total_income)}</p>
            </div>
            <div className="bg-white rounded-xl shadow p-5 border-l-4 border-red-500">
              <p className="text-xs text-gray-500">Total Expense (Group)</p>
              <p data-testid="group-pl-total-expense" className="text-2xl md:text-3xl font-bold text-red-600">{fmt(data.totals?.total_expense)}</p>
            </div>
            <div className={`bg-white rounded-xl shadow p-5 border-l-4 ${netPositive ? 'border-blue-500' : 'border-orange-500'}`}>
              <div className="flex items-center gap-2">
                {netPositive ? <TrendingUp className="h-4 w-4 text-blue-500" /> : <TrendingDown className="h-4 w-4 text-orange-500" />}
                <p className="text-xs text-gray-500">Net {netPositive ? 'Profit' : 'Loss'} (Group)</p>
              </div>
              <p data-testid="group-pl-net-profit" className={`text-2xl md:text-3xl font-bold ${netPositive ? 'text-blue-600' : 'text-orange-600'}`}>{fmt(Math.abs(data.totals?.net_profit || 0))}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow mb-6" data-testid="group-pl-trend-chart">
            <div className="p-4 border-b flex items-center justify-between">
              <h3 className="font-bold">12-Month Trend — Income vs Expense</h3>
              <span className="text-xs text-gray-400">{selectedCollege ? colleges.find(c => c.id === selectedCollege)?.name : 'All Colleges'}</span>
            </div>
            <div className="p-4" style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={trend} margin={{ top: 10, right: 20, bottom: 0, left: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat('en-IN', { notation: 'compact' }).format(v)} />
                  <Tooltip formatter={(v) => fmt(v)} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="income" name="Income" fill="#16a34a" radius={[3, 3, 0, 0]} />
                  <Bar dataKey="expense" name="Expense" fill="#dc2626" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="net" name="Net" stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow mb-6">
            <div className="p-4 border-b"><h3 className="font-bold">College-wise Profit &amp; Loss</h3></div>
            <div className="overflow-x-auto"><table className="w-full text-sm"><thead className="bg-gray-50"><tr>
              <th className="text-left p-3">College</th><th className="text-right p-3">Income</th><th className="text-right p-3">Expense</th><th className="text-right p-3">Net Profit/Loss</th>
            </tr></thead><tbody className="divide-y">
              {data.colleges?.map(c => (
                <tr key={c.college_id} className="hover:bg-gray-50" data-testid={`group-pl-row-${c.college_id}`}>
                  <td className="p-3 font-medium">{c.college_name}
                    {c.is_external && <span className="ml-2 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">🔗 External</span>}
                    {c.external_error && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">Unreachable</span>}
                  </td>
                  <td className="p-3 text-right text-green-700">{fmt(c.total_income)}</td>
                  <td className="p-3 text-right text-red-700">{fmt(c.total_expense)}</td>
                  <td className={`p-3 text-right font-bold ${c.net_profit >= 0 ? 'text-blue-700' : 'text-orange-600'}`}>{fmt(c.net_profit)}</td>
                </tr>
              ))}
              <tr className="bg-gray-100 font-bold">
                <td className="p-3">GROUP TOTAL</td>
                <td className="p-3 text-right text-green-700">{fmt(data.totals?.total_income)}</td>
                <td className="p-3 text-right text-red-700">{fmt(data.totals?.total_expense)}</td>
                <td className={`p-3 text-right ${netPositive ? 'text-blue-700' : 'text-orange-600'}`}>{fmt(data.totals?.net_profit)}</td>
              </tr>
            </tbody></table></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-xl shadow">
              <div className="p-4 border-b"><h3 className="font-bold text-green-700">Income by Category (Merged)</h3></div>
              {Object.keys(data.income_by_category || {}).length > 0 ? (
                <table className="w-full text-sm"><tbody className="divide-y">
                  {Object.entries(data.income_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <tr key={cat} className="hover:bg-gray-50"><td className="p-3">{cat}</td><td className="p-3 text-right font-medium text-green-700">{fmt(amt)}</td></tr>
                  ))}
                </tbody></table>
              ) : <p className="p-4 text-gray-400 text-sm">No income in this period</p>}
            </div>
            <div className="bg-white rounded-xl shadow">
              <div className="p-4 border-b"><h3 className="font-bold text-red-700">Expenses by Category (Merged)</h3></div>
              {Object.keys(data.expense_by_category || {}).length > 0 ? (
                <table className="w-full text-sm"><tbody className="divide-y">
                  {Object.entries(data.expense_by_category).sort((a, b) => b[1] - a[1]).map(([cat, amt]) => (
                    <tr key={cat} className="hover:bg-gray-50"><td className="p-3">{cat}</td><td className="p-3 text-right font-medium text-red-700">{fmt(amt)}</td></tr>
                  ))}
                </tbody></table>
              ) : <p className="p-4 text-gray-400 text-sm">No expenses in this period</p>}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupProfitLoss;
